import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Users, Building2, DollarSign, AlertTriangle, Briefcase, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";

const fmtMoney = (n) => n >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : `$${(n / 1e3).toFixed(0)}k`;

export default function NationalMap() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    if (user.role !== "admin") { navigate("/"); return; }
    api.get("/compliance/national-map").then((r) => setData(r.data)).catch(() => {});
  }, [user, loading, navigate]);

  if (loading || !data) return <div className="py-32 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-blue-800" /></div>;

  const totals = data.totals;
  const stats = [
    ["Caregivers", totals.caregivers.toLocaleString(), Users],
    ["Partner agencies", totals.agencies.toLocaleString(), Building2],
    ["Active clients", totals.clients.toLocaleString(), Briefcase],
    ["Platform GMV", fmtMoney(totals.gmv), DollarSign],
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-blue-800 mb-4 hover:gap-2.5 transition-all"><ArrowLeft className="w-4 h-4" /> Admin</Link>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <h1 className="font-display text-3xl font-bold text-blue-950">National Operations Map</h1>
        <span className="text-[11px] font-semibold uppercase tracking-wide bg-slate-200 text-slate-600 px-2.5 py-1 rounded-full">Demo data</span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="map-totals">
        {stats.map(([label, val, Icon]) => (
          <div key={label} className="bg-blue-950 text-white rounded-2xl p-5">
            <Icon className="w-6 h-6 text-teal-400 mb-3" />
            <p className="font-display text-3xl font-bold">{val}</p>
            <p className="text-blue-200 text-sm mt-1">{label}</p>
          </div>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="map-states">
        {data.states.map((s) => (
          <Link key={s.abbr} to={`/compliance/${s.state_slug}`} data-testid={`map-state-${s.abbr}`}
            className="bg-white rounded-2xl border border-blue-900/5 p-5 shadow-sm hover:shadow-md hover:border-teal-500 transition-all">
            <div className="flex items-center justify-between">
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-teal-50 text-teal-700 font-display font-bold">{s.abbr}</span>
              {s.alerts > 0 && <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-amber-100 text-amber-800 px-2 py-1 rounded-full"><AlertTriangle className="w-3 h-3" /> {s.alerts}</span>}
            </div>
            <p className="font-display font-bold text-blue-950 mt-3">{s.state_name}</p>
            <div className="mt-3 grid grid-cols-2 gap-y-1.5 text-sm">
              <span className="text-slate-500">Caregivers</span><span className="text-right font-semibold text-blue-950">{s.caregivers.toLocaleString()}</span>
              <span className="text-slate-500">Agencies</span><span className="text-right font-semibold text-blue-950">{s.agencies}</span>
              <span className="text-slate-500">GMV</span><span className="text-right font-semibold text-teal-700">{fmtMoney(s.gmv)}</span>
              <span className="text-slate-500">Opportunities</span><span className="text-right font-semibold text-blue-950">{s.opportunities}</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
